import React, {useCallback} from "react"
import classNames from "classnames"
import {Formik} from "formik"
import * as Yup from "yup"
import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import {usStates, usPostalCode} from "../../resources/address"
import {Account, DacaPolicies, enterDaca} from "../../resources/account"
import {useAccessToken} from "../../services/auth"
import {focusFirstInvalidField} from "../../components/Address/Address"
import Textfield from "../../components/Address/Textfield"
import Dropdown from "../../components/Address/Dropdown"
import Button from "../../components/Button/Button"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"

export type DACASettingsFormValues = {
	isBlockedDaca: boolean
	counterpartyType: "ach" | "wire"
	ach: {
		name: string
		routingNumber: string
		accountNumber: string
		accountType: string
	}
	wire: {
		name: string
		routingNumber: string
		accountNumber: string
		address: {
			street: string
			street2: string
			city: string
			state: string
			postalCode: string
		}
	}
}

interface DACASettingsModalProps {
	closeModal: () => void
	account: Account
	onSuccess: () => void
	onActivate?: () => Promise<void> | void
}

export function DACASettingsModal({closeModal, account, onSuccess, onActivate}: DACASettingsModalProps) {
	const accessToken = useAccessToken()
	const [enterDacaState, setEnterDacaState] = useAsyncResultIdle(enterDaca)

	const initialFormValues: DACASettingsFormValues = {
		isBlockedDaca: false,
		counterpartyType: "ach",
		ach: {
			name: "",
			routingNumber: "",
			accountNumber: "",
			accountType: "",
		},
		wire: {
			name: "",
			routingNumber: "",
			accountNumber: "",
			address: {
				street: "",
				street2: "",
				city: "",
				state: "",
				postalCode: "",
			},
		},
	}

	const validationSchema = Yup.object().shape({
		isBlockedDaca: Yup.boolean(),
		counterpartyType: Yup.string().oneOf(["ach", "wire"]),
		ach: Yup.object().when(["isBlockedDaca", "counterpartyType"], {
			is: (isBlockedDaca: boolean, counterpartyType: string) => isBlockedDaca && counterpartyType === "ach",
			then: () =>
				Yup.object().shape({
					name: Yup.string().required("Please enter counterparty name"),
					routingNumber: Yup.string()
						.required("Please enter routing number")
						.matches(/^\d{9}$/, "The routing number entered is invalid"),
					accountNumber: Yup.string()
						.required("Please enter account number")
						.matches(/^(\d{1,17}|\d{1,15}-\d{1,2})$/, "The account number entered is invalid"),
					accountType: Yup.string()
						.required("Please enter account type")
						.oneOf(["Checking", "Savings"], "The account type entered is invalid"),
				}),
			otherwise: () =>
				Yup.object().shape({
					name: Yup.string().notRequired(),
					routingNumber: Yup.string().notRequired(),
					accountNumber: Yup.string().notRequired(),
					accountType: Yup.string().notRequired(),
				}),
		}),
		wire: Yup.object().when(["isBlockedDaca", "counterpartyType"], {
			is: (isBlockedDaca: boolean, counterpartyType: string) => isBlockedDaca && counterpartyType === "wire",
			then: () =>
				Yup.object().shape({
					name: Yup.string().required("Please enter counterparty name"),
					routingNumber: Yup.string()
						.required("Please enter routing number")
						.matches(/^\d{9}$/, "The routing number entered is invalid"),
					accountNumber: Yup.string()
						.required("Please enter account number")
						.matches(/^(\d{1,17}|\d{1,15}-\d{1,2})$/, "The account number entered is invalid"),
					address: Yup.object().shape({
						street: Yup.string().required("Please enter your street"),
						street2: Yup.string().notRequired(),
						city: Yup.string().required("Please enter your city of residence"),
						state: Yup.string().required("Please select the state of residence"),
						postalCode: Yup.string()
							.required("Please enter your zip code (must be 5 or 9 digits long)")
							.matches(
								usPostalCode,
								"The zip code you have provided is invalid (US zip codes must be 5 or 9 digits long)"
							),
					}),
				}),
			otherwise: () =>
				Yup.object().shape({
					name: Yup.string().notRequired(),
					routingNumber: Yup.string().notRequired(),
					accountNumber: Yup.string().notRequired(),
					address: Yup.object().shape({
						street: Yup.string().notRequired(),
						street2: Yup.string().notRequired(),
						city: Yup.string().notRequired(),
						state: Yup.string().notRequired(),
						postalCode: Yup.string().notRequired(),
					}),
				}),
		}),
	})

	const buildDacaPolicies = (values: DACASettingsFormValues): DacaPolicies => {
		if (!values.isBlockedDaca) {
			return {purpose: ["manualTransfersByOperator"]}
		}

		if (values.counterpartyType === "ach") {
			return {
				purpose: ["manualTransfersByOperator", "scheduledAutoClear"],
				paymentMethod: "ach",
				achCounterparty: {
					name: values.ach.name,
					routingNumber: values.ach.routingNumber,
					accountNumber: values.ach.accountNumber,
					accountType: values.ach.accountType,
				},
			}
		}

		const hasAddress = Boolean(
			values.wire.address.street ||
				values.wire.address.city ||
				values.wire.address.state ||
				values.wire.address.postalCode
		)

		return {
			purpose: ["manualTransfersByOperator", "scheduledAutoClear"],
			paymentMethod: "wire",
			wireCounterparty: {
				name: values.wire.name,
				routingNumber: values.wire.routingNumber,
				accountNumber: values.wire.accountNumber,
				...(hasAddress
					? {
							address: {
								street: values.wire.address.street,
								city: values.wire.address.city,
								state: values.wire.address.state,
								postalCode: values.wire.address.postalCode,
								country: "US",
							},
					  }
					: {}),
			},
		}
	}

	const handleEnterDaca = (values: DACASettingsFormValues) => {
		const dacaPolicies = buildDacaPolicies(values)
		setEnterDacaState(accessToken, account, dacaPolicies)
	}

	const refreshAndClose = useCallback(() => {
		onSuccess()
		closeModal()
	}, [onSuccess, closeModal])

	const handleActivate = useCallback(async () => {
		await onActivate?.()
		refreshAndClose()
	}, [onActivate, refreshAndClose])

	return (
		<Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleEnterDaca}>
			{({values, handleChange, handleBlur, handleSubmit, errors, isValid, setFieldValue}) => (
				<AsyncResultModal
					title="DACA Settings"
					classname={classNames("card-action-modal", enterDacaState.isOk() && "daca-settings-modal-success")}
					close={refreshAndClose}
					buttonClassname="is-black"
					buttonText="Enter DACA"
					cancelButtonText="Cancel"
					cancelButtonClassname="is-white"
					state={enterDacaState}
					successText="Saved"
					errorToText={(err) => err.errors[0].title}
					onSubmit={(e: React.FormEvent<HTMLFormElement> | undefined) => {
						handleSubmit(e)
						if (!isValid) {
							focusFirstInvalidField(errors)
						}
					}}
					disableCloseOnSuccess={true}
					successComponent={() => (
						<>
							<section className="modal-card-body">
								<div className="daca-settings-success">
									<p className="daca-settings-success-title">DACA entered successfully</p>
									<p className="daca-settings-success-prompt">Would you like to activate DACA now?</p>
									<div className="daca-settings-success-helper">
										<p>
											Activating a Springing DACA enables manual transfers. Activating a Blocked DACA enables daily
											sweeps and manual transfers.
										</p>
									</div>
								</div>
							</section>
							<footer className="modal-card-foot">
								<Button type="button" theme="neutral" variant="outline" onClick={refreshAndClose}>
									Not now
								</Button>
								<Button type="button" theme="neutral" onClick={handleActivate}>
									Activate now
								</Button>
							</footer>
						</>
					)}
					blockBodyScroll={true}
				>
					<div className="daca-settings">
						<div className="daca-settings-options">
							<div className={classNames("daca-option-box", !values.isBlockedDaca && "daca-option-box--selected")}>
								<div className="daca-option-box__row">
									<label className="daca-option-box__label">
										<input
											type="radio"
											name="daca-settings-mode"
											checked={!values.isBlockedDaca}
											onChange={() => setFieldValue("isBlockedDaca", false)}
										/>
										<span className="daca-option-box__title">Springing DACA</span>
									</label>
									<div className="daca-option-box__subcopy">Supports manual transfers to the controlling party</div>
								</div>
							</div>

							<div className={classNames("daca-option-box", values.isBlockedDaca && "daca-option-box--selected")}>
								<div className="daca-option-box__row">
									<label className="daca-option-box__label">
										<input
											type="radio"
											name="daca-settings-mode"
											checked={values.isBlockedDaca}
											onChange={() => setFieldValue("isBlockedDaca", true)}
										/>
										<span className="daca-option-box__title">Blocked DACA</span>
									</label>
									<div className="daca-option-box__subcopy">
										Supports daily sweeps from the account to the controlling party
									</div>
								</div>

								{values.isBlockedDaca ? (
									<div className={classNames("daca-option-box__expanded", "daca-settings-panel")}>
										<div className="daca-settings-tabs">
											<button
												type="button"
												className={classNames("daca-settings-tab", values.counterpartyType === "ach" && "is-active")}
												onClick={() => setFieldValue("counterpartyType", "ach")}
											>
												ACH Counterparty
											</button>
											<button
												type="button"
												className={classNames("daca-settings-tab", values.counterpartyType === "wire" && "is-active")}
												onClick={() => setFieldValue("counterpartyType", "wire")}
											>
												Wire Counterparty
											</button>
										</div>
										{values.counterpartyType === "ach" ? (
											<div className="daca-counterparty-form">
												<div className="form-row one">
													<Textfield
														label="Counterparty name"
														placeholder="Add counterparty name"
														name="ach.name"
														value={values.ach.name}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<div className="form-row two">
													<Textfield
														label="Routing Number"
														placeholder="000000000"
														name="ach.routingNumber"
														value={values.ach.routingNumber}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
													<Textfield
														label="Account Number"
														placeholder="00000000000"
														name="ach.accountNumber"
														value={values.ach.accountNumber}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<div className="form-row one">
													<Dropdown
														name="ach.accountType"
														label="Account Type"
														value={values.ach.accountType}
														onChange={handleChange}
														onBlur={handleBlur}
													>
														<option value="" disabled>
															Select Account Type
														</option>
														<option value="Checking">Checking</option>
														<option value="Savings">Savings</option>
													</Dropdown>
												</div>
											</div>
										) : (
											<div className="daca-counterparty-form">
												<div className="form-row one">
													<Textfield
														label="Counterparty name"
														placeholder="Add counterparty name"
														name="wire.name"
														value={values.wire.name}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<div className="form-row two">
													<Textfield
														label="Routing Number"
														placeholder="000000000"
														name="wire.routingNumber"
														value={values.wire.routingNumber}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
													<Textfield
														label="Account Number"
														placeholder="00000000000"
														name="wire.accountNumber"
														value={values.wire.accountNumber}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												{/* NOTE: If/when international wire counterparties are supported, add Country field here. */}
												<div className="daca-counterparty-section-title">Address</div>
												<div className="form-row one">
													<Textfield
														label="Street"
														placeholder="Enter street address"
														name="wire.address.street"
														value={values.wire.address.street}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<div className="form-row two">
													<Textfield
														label="Apartment, unit, floor"
														placeholder="Apt, unit, floor (optional)"
														name="wire.address.street2"
														value={values.wire.address.street2}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
													<Textfield
														label="City"
														placeholder="Enter city"
														name="wire.address.city"
														value={values.wire.address.city}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
												<div className="form-row two">
													<Dropdown
														name="wire.address.state"
														label="State"
														value={values.wire.address.state}
														onChange={handleChange}
														onBlur={handleBlur}
													>
														<option value="" disabled>
															Select state
														</option>
														{Object.keys(usStates).map((state) => (
															<option key={state} value={state}>
																{usStates[state]}
															</option>
														))}
													</Dropdown>
													<Textfield
														label="Zip Code"
														placeholder="Enter zip code"
														name="wire.address.postalCode"
														value={values.wire.address.postalCode}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</div>
											</div>
										)}
									</div>
								) : null}
							</div>
						</div>
					</div>
				</AsyncResultModal>
			)}
		</Formik>
	)
}
